import type { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { CreateBox } from "@babylonjs/core/Meshes/Builders/boxBuilder";
import { CreateTorus } from "@babylonjs/core/Meshes/Builders/torusBuilder";
import { CreateCylinder } from "@babylonjs/core/Meshes/Builders/cylinderBuilder";
import { CreateIcoSphere } from "@babylonjs/core/Meshes/Builders/icoSphereBuilder";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { CreateSphere } from "@babylonjs/core/Meshes/Builders/sphereBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { PropStatus, propSolid, destructionStage, type ArenaBox, type ArenaProp, type PropState } from "@arena/shared";
import { surfaceRepeat, surfaceTexture, type SurfaceKind } from "./pixelTextures.ts";
import { segmentHitsBox } from "./geometry.ts";

/**
 * The objects of the map (D39), drawn until their PixelLab art arrives:
 * big things (boulders, columns, braziers, trees, the Gate) as solid shapes
 * with pixel textures, small things to throw (stones, skulls) as pixel
 * sprites. Collected objects disappear until thrown or dropped; broken
 * objects remain hidden until they respawn.
 */

interface PropVisual {
  def: ArenaProp;
  root: TransformNode;
  /** Material that flashes when hit and darkens as the object wears out. */
  materials: StandardMaterial[];
  /** Fire of a brazier, hidden when it breaks. */
  flame: Mesh | null;
  /** A brazier: its torch light goes out when it breaks. */
  fire: boolean;
  /** What is left while it is broken (the stump of a tree), if it has art for it. */
  remains: Mesh | null;
  /** Ground-only pickup marker: never follows an object into someone's hands. */
  pickupGlow: Mesh | null;
  shake: number;
  damageArt?: {plane:Mesh;textures:Texture[];stage:number;scars:Mesh[]};
  warning?:Mesh;
}

/**
 * Objects painted in PixelLab (D39), in /proto2d/props/: drawn as upright
 * sprites (like the fighters) `height` metres tall. `remains` is the art shown
 * where a broken one stood until it comes back.
 */
const ART: Record<string, { file: string; w: number; h: number; height: number; remains?: { file: string; w: number; h: number; height: number } }> = {
  crypt: {file:'/proto2d/inferno-rich/crypt-0.png',w:128,h:128,height:4.6,remains:{file:'/proto2d/inferno-rich/crypt-3.png',w:128,h:128,height:4.6}},
  statue: {file:'/proto2d/inferno-rich/statue-0.png',w:128,h:128,height:4.8,remains:{file:'/proto2d/inferno-rich/statue-3.png',w:128,h:128,height:4.8}},
  shack: {file:'/proto2d/inferno-rich/shack-0.png',w:128,h:128,height:4,remains:{file:'/proto2d/inferno-rich/shack-3.png',w:128,h:128,height:4}},
  cargo: {file:'/proto2d/inferno-rich/cargo-0.png',w:128,h:128,height:1.8},
  masso: {file:'/proto2d/inferno-rich/boulder-0.png',w:128,h:128,height:0},
  albero: { file: "albero.png", w: 64, h: 128, height: 4.2, remains: { file: "albero-rotto.png", w: 64, h: 64, height: 1.6 } },
  braciere: { file: "braciere.png", w: 48, h: 64, height: 1.8 },
  colonna: { file: "colonna.png", w: 48, h: 96, height: 0 },
  sasso: { file: "sasso.png", w: 32, h: 32, height: 0.6 },
  teschio: { file: "teschio.png", w: 32, h: 32, height: 0.55 },
};

interface Debris {
  mesh: Mesh;
  v: Vector3;
  spin: Vector3;
  age: number;
}

/** Pixel art of the small objects (sprites), one string per row. */
const SPRITES: Record<string, { rows: string[]; palette: Record<string, string> }> = {
  sasso: {
    rows: ["...gggg...", "..gGGGgg..", ".gGGgggdg.", "gGgggggddg", "ggggggdddg", "gggggdddd.", ".gddddddd.", "..ddddd..."],
    palette: { G: "#9a9088", g: "#6e655e", d: "#463f3a" },
  },
  teschio: {
    rows: ["..wwwwww..", ".wwwwwwww.", "wwwwwwwwww", "wkkwwwkkww", "wkkwwwkkwg", "wwwwkwwwwg", ".wwwwwwwg.", "..wkwkwg..", "..wwwwg..."],
    palette: { w: "#e8dcc4", g: "#a89c84", k: "#1a1210" },
  },
};

/** Fire for braziers and torches: drawn, not lit (the lights are the atmosphere's). */
const FLAME = "#ffae42";

export class PropsView {
  private readonly scene: Scene;
  private readonly visuals = new Map<string, PropVisual>();
  private readonly debris: Debris[] = [];
  private readonly debrisMaterial: StandardMaterial;
  private readonly materials = new Map<string, StandardMaterial>();
  private readonly artTextures = new Map<string,Texture>();
  private readonly baseTints=new WeakMap<StandardMaterial,Color3>();
  private readonly gateOccluders: { box: ArenaBox; materials: StandardMaterial[] }[] = [];
  private time = 0;

  constructor(scene: Scene, props: readonly ArenaProp[]) {
    this.scene = scene;
    this.debrisMaterial = this.surface("debris", "rock");
    for (const def of props) this.visuals.set(def.id, this.build(def));
  }

  /** Flames of the braziers, for the atmosphere's torch lights. */
  get firePoints(): Vector3[] {
    return [...this.visuals.values()].filter((v) => v.fire).map((v) => new Vector3(v.def.x, v.def.y + v.def.size.y + 0.25, v.def.z));
  }

  /** Index of each brazier in `firePoints`, by prop id. */
  get fireIds(): string[] {
    return [...this.visuals.values()].filter((v) => v.fire).map((v) => v.def.id);
  }

  /** Every mesh standing on the floor: they cast shadows. */
  get casters(): Mesh[] {
    // Upright sprites turn with the camera: their shadow would spin, the fighters' blob shadows are enough.
    return [...this.visuals.values()].flatMap((v) => (v.root.getChildMeshes(false) as Mesh[]).filter((m) => !m.name.startsWith("art-") && !m.name.endsWith("-glow")));
  }

  /** A hit that did not break it: a short shake. */
  hit(id: string): void {
    const v = this.visuals.get(id);
    if (v) v.shake = 0.25;
  }

  /** Powerful moves also shake nearby scenery when no fighter is hit. */
  disturb(x: number, z: number, radius: number): void {
    for (const v of this.visuals.values()) {
      if ((v.root.position.x - x) ** 2 + (v.root.position.z - z) ** 2 < radius * radius) v.shake = 0.25;
    }
  }

  /** Tall gate pillars fade independently when they cover the local fighter. */
  updateOcclusion(camera: Vector3, focus: Vector3 | null, dt: number): void {
    for (const { box, materials } of this.gateOccluders) {
      const target = focus && segmentHitsBox(camera, focus, box, 0.35) ? 0.15 : 1;
      for (const material of materials) {
        material.alpha += (target - material.alpha) * (1 - Math.exp(-dt * 10));
        if (Math.abs(material.alpha - target) < 0.005) material.alpha = target;
      }
    }
  }

  /** Breaks into flying chunks of its own material. */
  shatter(id: string, x: number, y: number, z: number): void {
    const v = this.visuals.get(id);
    const big = v?.def.blocks ?? false;
    const count = Math.min(big ? 10 : 6,96-this.debris.length);
    const material = v?.materials[0] ?? this.debrisMaterial;
    for (let i = 0; i < count; i++) {
      const mesh = CreateBox("debris", { size: big ? 0.18 + Math.random() * 0.2 : 0.1 + Math.random() * 0.08 }, this.scene);
      mesh.material = material;
      mesh.isPickable = false;
      mesh.position.set(x + (Math.random() - 0.5) * 0.4, y, z + (Math.random() - 0.5) * 0.4);
      const a = Math.random() * Math.PI * 2;
      const speed = 2 + Math.random() * 3;
      this.debris.push({
        mesh,
        v: new Vector3(Math.cos(a) * speed, 3 + Math.random() * 4, Math.sin(a) * speed),
        spin: new Vector3(Math.random() * 10, Math.random() * 10, Math.random() * 10),
        age: 0,
      });
    }
  }

  /** A mesh for a thrown object in flight (the caller moves and disposes it). */
  flying(kind: string): Mesh {
    const art = ART[kind];
    if (art) return this.artSprite(kind, art.file, art.w, art.h, art.height);
    const sprite = SPRITES[kind];
    if (sprite) return this.sprite(kind, sprite, 0.55);
    const mesh = CreateIcoSphere("thrown", { radius: 0.3, subdivisions: 1, flat: true }, this.scene);
    mesh.material = this.debrisMaterial;
    return mesh;
  }

  /** One frame. Carried props are inventory only, with no mesh on the fighter. */
  update(dt: number, state: { get(id: string): PropState | undefined }): void {
    this.time += dt;
    for (const [id, v] of this.visuals) {
      const st = state.get(id);
      if (!st) continue;
      const gone = st.status === PropStatus.Gone;
      const carried = st.status === PropStatus.Carried;
      if(v.warning){
        v.warning.setEnabled(st.collapseTicks>0 && !gone);
        (v.warning.material as StandardMaterial).alpha=.22+.2*(.5+.5*Math.sin(this.time*22));
      }
      if(v.damageArt){
        v.damageArt.scars.forEach((scar,i)=>scar.setEnabled(!gone && st.hp<v.def.hp*(1-i/8)));
        const stage=destructionStage(st.hp,v.def.hp,gone);
        if(stage!==v.damageArt.stage && stage<3){
          (v.damageArt.plane.material as StandardMaterial).diffuseTexture=v.damageArt.textures[stage]!;
        }
        v.damageArt.stage=stage;
      }
      v.pickupGlow?.setEnabled(!gone && !carried);
      v.remains?.setEnabled(gone);
      if (gone || carried) {
        v.root.setEnabled(false);
        continue;
      }
      v.root.setEnabled(true);
      v.root.position.set(st.x, st.y, st.z);
      // Hit: a short shake; worn: darker.
      v.shake = Math.max(0, v.shake - dt);
      const jolt = v.shake > 0 ? Math.sin(this.time * 70) * 0.06 * (v.shake / 0.25) : 0;
      v.root.position.x += jolt;
      if(st.collapseTicks>0 && v.def.collapse){
        const progress=1-st.collapseTicks/v.def.collapse.warningTicks;
        const fall=Math.max(0,(progress-.65)/.35);
        v.root.rotation.z=(.08*progress+fall*1.2)*-Math.sign(v.def.collapse.dx);
      }else v.root.rotation.z=0;
      if (v.def.hp > 0) {
        const worn = v.damageArt ? 1 : 0.55 + 0.45 * (st.hp / v.def.hp);
        const flash = v.shake > 0.15 ? 0.5 : 0;
        for (const m of v.materials) {
          m.emissiveColor.set(flash, flash * 0.6, flash * 0.3);
          if (m.name.startsWith("art-")) m.emissiveColor.set(0.72 * worn + flash, 0.68 * worn + flash * 0.6, 0.64 * worn + flash * 0.3);
          else {
            let tint=this.baseTints.get(m);
            if(!tint){tint=m.diffuseColor.clone();this.baseTints.set(m,tint);}
            m.diffuseColor.set(tint.r*worn,tint.g*worn,tint.b*worn);
          }
        }
      }
      const glow = this.materials.get("glow");
      if (glow) glow.alpha = 0.45 + 0.25 * Math.sin(this.time * 3);
      if (v.flame) v.flame.scaling.y = 1.2 + 0.25 * Math.sin(this.time * 17 + v.def.x);
    }
    for (let i = this.debris.length - 1; i >= 0; i--) {
      const d = this.debris[i]!;
      d.age += dt;
      d.v.y -= 18 * dt;
      d.mesh.position.addInPlace(d.v.scale(dt));
      d.mesh.rotation.addInPlace(d.spin.scale(dt));
      if (d.mesh.position.y < 0.05 && d.v.y < 0) {
        d.mesh.position.y = 0.05;
        d.v.scaleInPlace(0.4);
        d.v.y = Math.abs(d.v.y) * 0.3;
      }
      if (d.age > 1.4) {
        d.mesh.dispose();
        this.debris.splice(i, 1);
      }
    }
  }

  // -------------------------------------------------------------------------

  private build(def: ArenaProp): PropVisual {
    const root = new TransformNode(`prop-${def.id}`, this.scene);
    root.position.set(def.x, def.y, def.z);
    const { x: w, y: h, z: d } = def.size;
    const materials: StandardMaterial[] = [];
    let flame: Mesh | null = null;
    let pickupGlow: Mesh | null = null;
    const own = (name: string, kind: SurfaceKind, u = 1, v = 1) => {
      const m = this.surface(`${def.id}-${name}`, kind, u, v);
      materials.push(m);
      return m;
    };
    const add = (mesh: Mesh, material: StandardMaterial, y: number) => {
      mesh.parent = root;
      mesh.position.y = y;
      mesh.material = material;
      mesh.isPickable = false;
      mesh.receiveShadows = true;
      return mesh;
    };
    // A little variety: the same kind is never placed twice the same way.
    const turn = ((def.x * 7.3 + def.z * 3.1) % 6.28 + 6.28) % 6.28;

    const art = ART[def.kind];
    if (art) {
      const plane = this.artSprite(def.id, art.file, art.w, art.h, art.height || (def.kind==='masso'?Math.max(w,d)*1.5:h));
      plane.parent = root;
      materials.push(plane.material as StandardMaterial);
      if(def.kind==='shack'){
        // Reuse the painted doorway as a facade; its old painted roof is cropped
        // out. The new roof is a horizontal mesh at the exact collision height.
        plane.billboardMode=Mesh.BILLBOARDMODE_NONE;
        plane.scaling.set(w/art.height,h/art.height,1);
        plane.position.set(0,h/2,-d/2-.025);
        plane.setVerticesData('uv',[.12,.04,.88,.04,.88,.48,.12,.48]);
        const siding=own('walls','weatheredWood',w/2,h/2);
        add(CreateBox(`${def.id}-walls`,{width:w,height:h-.14,depth:d},this.scene),siding,(h-.14)/2);
        add(CreateBox(`${def.id}-roof`,{width:w,height:.14,depth:d},this.scene),own('roof','slate',w/2,d/2),h-.07);
        const timber=own('timbers','weatheredWood',1,2);timber.diffuseColor.set(.67,.65,.61);
        for(const x of [-w/2+.07,w/2-.07])for(const z of [-d/2-.01,d/2+.01]){
          const post=add(CreateBox(`${def.id}-post`,{width:.16,height:h,depth:.16},this.scene),timber,h/2);
          post.position.x=x;post.position.z=z;
        }
        for(const z of [-d/2,d/2]){
          const beam=add(CreateBox(`${def.id}-beam`,{width:w,height:.16,depth:.19},this.scene),timber,h-.22);
          beam.position.z=z;
        }
        const chimney=add(CreateBox(`${def.id}-chimney`,{width:.45,height:.65,depth:.45},this.scene),own('chimney','brick',1,1),h+.325);
        chimney.position.set(w/2-.55,h+.325,d/2-.5);
      }
      let remains: Mesh | null = null;
      if (art.remains) {
        remains = this.artSprite(`${def.id}-remains`, art.remains.file, art.remains.w, art.remains.h, art.remains.height);
        remains.position.set(def.x, def.y + art.remains.height / 2, def.z);
        remains.setEnabled(false);
      }
      let damageArt:PropVisual['damageArt'];
      let warning:Mesh|undefined;
      if(['crypt','statue','shack'].includes(def.kind)){
        const scars:Mesh[]=[];
        for(let i=0;i<7;i++){
          const texture=new DynamicTexture(`${def.id}-crack-${i}`,{width:128,height:128},this.scene,false,Texture.NEAREST_SAMPLINGMODE);
          const g=texture.getContext() as unknown as CanvasRenderingContext2D;
          const cx=def.kind==='statue'?59+(i%3)*4:43+(i%4)*12,cy=43+(i%3)*17;
          g.clearRect(0,0,128,128);g.lineWidth=2;g.strokeStyle='#201610';g.beginPath();g.moveTo(cx,cy);
          g.lineTo(cx-4,cy+7);g.lineTo(cx+2,cy+13);g.lineTo(cx-2,cy+22);g.stroke();
          texture.hasAlpha=true;texture.update();
          const m=new StandardMaterial(`${def.id}-scar-${i}`,this.scene);m.diffuseTexture=texture;m.useAlphaFromDiffuseTexture=true;
          m.disableLighting=true;m.emissiveColor.set(.7,.65,.6);m.backFaceCulling=false;
          const scar=CreatePlane(`${def.id}-scar-${i}`,{width:art.height,height:art.height},this.scene);
          scar.parent=root;scar.position.set(0,art.height/2,-.025-i*.001);scar.billboardMode=Mesh.BILLBOARDMODE_Y;
          if(def.kind==='shack'){
            scar.billboardMode=Mesh.BILLBOARDMODE_NONE;
            scar.scaling.set(w/art.height,h/art.height,1);
            scar.position.set(0,h/2,-d/2-.04-i*.001);
          }
          scar.material=m;scar.isPickable=false;scar.setEnabled(false);scars.push(scar);
        }
        damageArt={plane,textures:[0,1,2].map(stage=>this.artTexture(`/proto2d/inferno-rich/${def.kind}-${stage}.png`)),stage:0,scars};
        this.gateOccluders.push({box:propSolid(def),materials});
      }
      if(def.collapse){
        const c=def.collapse,len=Math.hypot(c.dx,c.dz);
        warning=CreateBox(`${def.id}-collapse-warning`,{width:c.width+.7,height:.025,depth:c.length+c.width+.7},this.scene);
        warning.position.set(def.x+c.dx/len*c.length/2,def.y+.035,def.z+c.dz/len*c.length/2);
        warning.rotation.y=Math.atan2(c.dx,c.dz);warning.isPickable=false;
        const material=new StandardMaterial(`${def.id}-danger`,this.scene);
        material.disableLighting=true;material.emissiveColor=Color3.FromHexString('#ef9a42');material.alpha=.3;
        warning.material=material;warning.setEnabled(false);
      }
      if (def.carry) {
        // Things to pick up say so: a soft light on the ground under them.
        const ring = CreateTorus(`${def.id}-glow`, { diameter: 0.75, thickness: 0.05, tessellation: 24 }, this.scene);
        ring.position.y = 0.03;
        ring.parent = root;
        ring.material = this.glow();
        ring.isPickable = false;
        pickupGlow = ring;
      }
      return { def, root, materials, flame: null, fire: def.kind === "braciere", remains, pickupGlow, shake: 0,damageArt,warning };
    }

    switch (def.kind) {
      case "masso": {
        const rock = add(CreateIcoSphere(def.id, { radius: 0.5, subdivisions: 1, flat: true }, this.scene), this.shared("rock"), h * 0.45);
        rock.scaling.set(w * 1.1, h * 1.05, d * 1.1);
        rock.rotation.y = turn;
        break;
      }
      case "rupe": {
        add(CreateBox(def.id, { width: w, height: h, depth: d }, this.scene), this.shared("rock", 2, 1), h / 2);
        // Its top is the same burnt ground as the plateau.
        add(CreateBox(`${def.id}-top`, { width: w - 0.1, height: 0.06, depth: d - 0.1 }, this.scene), this.shared("ash"), h + 0.01);
        const face=this.artSprite(`${def.id}-strata`,'/proto2d/inferno-v2/ledge.png',192,128,3);
        face.billboardMode=Mesh.BILLBOARDMODE_NONE;face.parent=root;face.position.set(0,.57,-d/2-.025);
        const side=this.artSprite(`${def.id}-strata-side`,'/proto2d/inferno-v2/ledge.png',192,128,2.1);
        side.billboardMode=Mesh.BILLBOARDMODE_NONE;side.parent=root;side.rotation.y=-Math.PI/2;side.position.set(-w/2-.025,.76,0);
        for(let i=0;i<5;i++){
          const chip=add(CreateIcoSphere(`${def.id}-rim`,{radius:.18,subdivisions:1},this.scene),this.shared('rock'),h-.08);
          chip.position.x=-w/2+.35+i*(w-.7)/4;chip.position.z=d/2-.12;
          chip.scaling.set(1.4,.65,.8);
        }
        break;
      }
      case "rovine":
        add(CreateBox(def.id, { width: w, height: h, depth: d }, this.scene), this.shared("rock", Math.max(w, d) / 2, h / 2), h / 2);
        break;
      case "porta":
        add(CreateBox(def.id, { width: w, height: h, depth: d }, this.scene), own("gate-brick", "brick", 1, h / 2), h / 2);
        add(CreateBox(`${def.id}-base`, { width: w + 0.3, height: 0.5, depth: d + 0.3 }, this.scene), own("gate-base", "rock"), 0.25);
        this.gateOccluders.push({ box: propSolid(def), materials });
        break;
      case "colonna": {
        const m = own("shaft", "stone", 1, h / 2);
        add(CreateBox(`${def.id}-base`, { width: w + 0.2, height: 0.3, depth: d + 0.2 }, this.scene), m, 0.15);
        const shaft = add(CreateCylinder(def.id, { height: h - 0.3, diameterTop: w * 0.7, diameterBottom: w * 0.85, tessellation: 10 }, this.scene), m, 0.3 + (h - 0.3) / 2);
        shaft.rotation.y = turn;
        // The broken top: a slanted cut.
        const stub = add(CreateCylinder(`${def.id}-stub`, { height: 0.35, diameter: w * 0.7, tessellation: 10 }, this.scene), m, h + 0.05);
        stub.rotation.z = 0.5;
        stub.position.x = 0.08;
        break;
      }
      case "braciere": {
        const m = own("iron", "iron");
        add(CreateCylinder(`${def.id}-leg`, { height: h - 0.35, diameter: 0.14, tessellation: 6 }, this.scene), m, (h - 0.35) / 2);
        add(CreateCylinder(`${def.id}-foot`, { height: 0.12, diameterTop: 0.2, diameterBottom: w, tessellation: 8 }, this.scene), m, 0.06);
        add(CreateCylinder(def.id, { height: 0.35, diameterTop: w, diameterBottom: w * 0.45, tessellation: 10 }, this.scene), m, h - 0.17);
        flame = add(CreateSphere(`${def.id}-fire`, { diameter: w * 0.75, segments: 4 }, this.scene), this.fire(), h + 0.2);
        flame.receiveShadows = false;
        break;
      }
      case "albero": {
        const m = own("bark", "bark");
        const trunk = add(CreateCylinder(def.id, { height: h, diameterTop: 0.18, diameterBottom: w * 0.55, tessellation: 7 }, this.scene), m, h / 2);
        trunk.rotation.z = 0.08;
        for (const [y, a, len] of [[h * 0.55, 0.9, 1.2], [h * 0.72, -1.1, 1], [h * 0.85, 2.4, 0.8]] as const) {
          const branch = add(CreateCylinder(`${def.id}-branch`, { height: len, diameterTop: 0.04, diameterBottom: 0.12, tessellation: 5 }, this.scene), m, y);
          branch.rotation.set(0, turn + a, 1.0);
          branch.position.x = Math.cos(turn + a) * 0.3;
          branch.position.z = -Math.sin(turn + a) * 0.3;
        }
        break;
      }
      default: {
        const sprite = SPRITES[def.kind];
        if (sprite) {
          const plane = this.sprite(def.kind, sprite, Math.max(w, 0.5) * 1.1);
          plane.parent = root;
          plane.position.y = 0.3;
        } else {
          add(CreateBox(def.id, { width: w, height: h, depth: d }, this.scene), this.shared("stone"), h / 2);
        }
      }
    }
    return { def, root, materials, flame, fire: def.kind === "braciere", remains: null, pickupGlow, shake: 0 };
  }

  /** A PixelLab picture standing upright, `height` metres tall, its base on the ground. */
  private artSprite(name: string, file: string, w: number, h: number, height: number): Mesh {
    const texture = this.artTexture(file.startsWith('/')?file:`/proto2d/props/${file}`);
    const material = new StandardMaterial(`art-${name}`, this.scene);
    material.diffuseTexture = texture;
    material.useAlphaFromDiffuseTexture = true;
    material.specularColor = Color3.Black();
    // Lit by the scene like the fighters, with a floor so the dark art never sinks into the night.
    material.emissiveColor = new Color3(0.72, 0.68, 0.64);
    material.backFaceCulling = false;
    material.maxSimultaneousLights = 8;
    const plane = CreatePlane(`art-${name}`, { width: (height * w) / h, height }, this.scene);
    plane.billboardMode = Mesh.BILLBOARDMODE_Y;
    plane.position.y = height / 2;
    plane.material = material;
    plane.isPickable = false;
    return plane;
  }

  private artTexture(file:string):Texture {
    let texture=this.artTextures.get(file);
    if(!texture){
      texture=new Texture(file,this.scene,true,true,Texture.NEAREST_SAMPLINGMODE);
      texture.hasAlpha=true;texture.wrapU=texture.wrapV=Texture.CLAMP_ADDRESSMODE;
      this.artTextures.set(file,texture);
    }
    return texture;
  }

  private sprite(kind: string, art: { rows: string[]; palette: Record<string, string> }, width: number): Mesh {
    const rows = art.rows;
    const texture = new DynamicTexture(`sprite-${kind}`, { width: rows[0]!.length, height: rows.length }, this.scene, false, Texture.NEAREST_SAMPLINGMODE);
    const g = texture.getContext() as unknown as CanvasRenderingContext2D;
    g.clearRect(0, 0, rows[0]!.length, rows.length);
    rows.forEach((row, y) =>
      [...row].forEach((ch, x) => {
        const c = art.palette[ch];
        if (!c) return;
        g.fillStyle = c;
        g.fillRect(x, y, 1, 1);
      }),
    );
    texture.update(false);
    texture.hasAlpha = true;
    const material = new StandardMaterial(`sprite-${kind}`, this.scene);
    material.diffuseTexture = texture;
    material.useAlphaFromDiffuseTexture = true;
    material.specularColor = Color3.Black();
    material.emissiveColor = new Color3(0.35, 0.3, 0.28);
    material.backFaceCulling = false;
    const plane = CreatePlane(`sprite-${kind}`, { width, height: width * (rows.length / rows[0]!.length) }, this.scene);
    plane.billboardMode = Mesh.BILLBOARDMODE_Y;
    plane.material = material;
    plane.isPickable = false;
    return plane;
  }

  /** One material per surface and tiling, shared by every object that does not wear out. */
  private shared(kind: SurfaceKind, u = 1, v = 1): StandardMaterial {
    const key = `${kind}:${u}:${v}`;
    let m = this.materials.get(key);
    if (!m) {
      m = this.surface(key, kind, u, v);
      // Obstacles read against the darker ground (D39): a little light of their own.
      m.emissiveColor = new Color3(0.2, 0.17, 0.15);
      this.materials.set(key, m);
    }
    return m;
  }

  private surface(name: string, kind: SurfaceKind, u = 1, v = 1): StandardMaterial {
    const material = new StandardMaterial(name, this.scene);
    const texture = surfaceTexture(this.scene, kind);
    texture.uScale = u * surfaceRepeat(kind);
    texture.vScale = v * surfaceRepeat(kind);
    material.diffuseTexture = texture;
    material.specularColor = Color3.Black();
    material.maxSimultaneousLights = 8;
    return material;
  }

  private glow(): StandardMaterial {
    let m = this.materials.get("glow");
    if (!m) {
      m = new StandardMaterial("glow", this.scene);
      m.disableLighting = true;
      m.emissiveColor = Color3.FromHexString("#ffd58a");
      m.alpha = 0.6;
      this.materials.set("glow", m);
    }
    return m;
  }

  private fire(): StandardMaterial {
    let m = this.materials.get("fire");
    if (!m) {
      m = new StandardMaterial("fire", this.scene);
      m.disableLighting = true;
      m.emissiveColor = Color3.FromHexString(FLAME);
      this.materials.set("fire", m);
    }
    return m;
  }
}
